import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Pagination } from 'react-bootstrap';
import { Box, Typography, Paper, Tooltip } from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { Spinner } from 'reactstrap';
import {
  getRecurringInventoryItems,
  createRecurringInventoryItems,
  updateRecurringInventoryItem,
  moveRecurringInventoryItem,
  generateRecurringInventoryReport,
  uploadRecurringInventoryInvoice,
} from './FormikHelpers/Api';
import './recurring-inventory.scss';

const LS_KEY = 'ri_table_col_widths';

const FACTORY_WIDTHS = {
  expand: 44, name: 160, requestsSupported: 180, make: 120, modelNo: 130, sku: 130,
  costPerUnit: 110, qty_active: 90, qty_min: 80, qty_max: 80,
  location_room: 130, warrantyExpiry: 130, actions: 120,
};

const COL_LABELS = {
  expand: '', name: 'Name', requestsSupported: 'Requests Supported', make: 'Make',
  modelNo: 'Model No.', sku: 'SKU', costPerUnit: 'Cost/Unit',
  qty_active: 'Qty (Active)', qty_min: 'Min Qty', qty_max: 'Max Qty',
  location_room: 'Room', warrantyExpiry: 'Warranty Exp.', actions: '',
};

const loadSavedWidths = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? { ...FACTORY_WIDTHS, ...JSON.parse(raw) } : { ...FACTORY_WIDTHS };
  } catch { return { ...FACTORY_WIDTHS }; }
};

const saveWidths = (w) => { try { localStorage.setItem(LS_KEY, JSON.stringify(w)); } catch { } };

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const fmtCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 }).format(v);
};

const cap = (s) => {
  if (!s && s !== 0) return '—';
  const str = String(s).trim();
  if (!str) return '—';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const getWarrantyExpiry = (item) => {
  const dates = (item.warrantyDetails || [])
    .map((w) => w.warrantyExpirationDate)
    .filter(Boolean)
    .map((d) => new Date(d));
  if (!dates.length) return null;
  return new Date(Math.max(...dates));
};

const isExpiringSoon = (date) => {
  if (!date) return false;
  const diff = date - new Date();
  return diff > 0 && diff < 90 * 24 * 60 * 60 * 1000;
};

const isExpired = (date) => {
  if (!date) return false;
  return date < new Date();
};

const buildSelectStyles = () => ({
  menuPortal: (b) => ({ ...b, zIndex: 99999 }),
  container: (b) => ({ ...b, width: '100%' }),
  control: (b, { isFocused }) => ({
    ...b, minHeight: 32, fontSize: '0.78rem', borderRadius: 7,
    borderColor: isFocused ? '#1D4ED8' : '#CBD5E1', borderWidth: '1.5px',
    boxShadow: isFocused ? '0 0 0 3px #DBEAFE' : 'none',
    '&:hover': { borderColor: '#1D4ED8' },
    backgroundColor: '#EFF6FF', cursor: 'pointer',
  }),
  valueContainer: (b) => ({ ...b, padding: '0 10px' }),
  dropdownIndicator: (b) => ({ ...b, padding: '0 6px', color: '#94A3B8' }),
  indicatorSeparator: () => ({ display: 'none' }),
  multiValue: (b) => ({ ...b, background: '#DBEAFE', borderRadius: 5, fontSize: '0.72rem' }),
  multiValueLabel: (b) => ({ ...b, color: '#1D4ED8', fontWeight: 600, padding: '1px 5px' }),
  multiValueRemove: (b) => ({ ...b, color: '#1D4ED8', ':hover': { background: '#BFDBFE', color: '#1E40AF' } }),
  menu: (b) => ({ ...b, fontSize: '0.78rem', borderRadius: 8, zIndex: 9999, boxShadow: '0 12px 40px rgba(15,28,46,0.12)', border: '1px solid #E2E8F0' }),
  option: (b, { isSelected, isFocused }) => ({
    ...b,
    backgroundColor: isSelected ? '#1D4ED8' : isFocused ? '#EFF6FF' : 'white',
    color: isSelected ? 'white' : '#0F172A', fontWeight: isSelected ? 600 : 400,
    padding: '7px 12px', borderRadius: 5, margin: '1px 3px', width: 'calc(100% - 6px)',
  }),
  singleValue: (b) => ({ ...b, color: '#0F172A', fontWeight: 600, fontSize: '0.78rem' }),
  placeholder: (b) => ({ ...b, color: '#94A3B8', fontSize: '0.78rem' }),
});

const WarrantyBadge = ({ item }) => {
  const expiry = getWarrantyExpiry(item);
  if (!expiry) return <span className="ri-cell-muted">—</span>;
  if (isExpired(expiry)) return <span className="ri-badge ri-badge--expired"><FeatherIcon icon="alert-triangle" size={10} /> Expired</span>;
  if (isExpiringSoon(expiry)) return <span className="ri-badge ri-badge--expiring"><FeatherIcon icon="clock" size={10} /> {fmtDate(expiry)}</span>;
  return <span className="ri-badge ri-badge--warranty-ok"><FeatherIcon icon="shield" size={10} /> {fmtDate(expiry)}</span>;
};

const SkuBadge = ({ sku }) => {
  if (!sku || !sku.length) return <span className="ri-cell-muted">—</span>;
  return (
    <div className="ri-sku-list">
      {sku.slice(0, 2).map((s, i) => <span key={i} className="ri-sku-chip">{s}</span>)}
      {sku.length > 2 && <span className="ri-sku-chip ri-sku-chip--more">+{sku.length - 2}</span>}
    </div>
  );
};

const RequestsBadge = ({ ids, requestTypeOptions }) => {
  if (!ids || !ids.length) return <span className="ri-cell-muted">—</span>;
  const labels = ids.map((id) => {
    const opt = requestTypeOptions.find((o) => o.value === (id?._id || id));
    return opt?.label || String(id?._id || id).slice(-6);
  });
  return (
    <div className="ri-req-list">
      {labels.slice(0, 2).map((l, i) => <span key={i} className="ri-req-chip">{l}</span>)}
      {labels.length > 2 && <span className="ri-req-chip ri-req-chip--more">+{labels.length - 2}</span>}
    </div>
  );
};

const ResizeHandle = ({ onResize }) => {
  const dragging = useRef(false);
  const startX = useRef(0);
  const onDown = (e) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    const onMove = (ev) => { if (dragging.current) { onResize(ev.clientX - startX.current); startX.current = ev.clientX; } };
    const onUp = () => { dragging.current = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  return <div className="ri-resize-handle" onMouseDown={onDown}><div className="ri-rhandle" /></div>;
};

const FInput = ({ value, onChange, type = 'text', placeholder, min, step, readOnly = false }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type} value={value ?? ''} min={min} step={step}
      onChange={(e) => !readOnly && onChange(e.target.value)}
      placeholder={placeholder} onClick={(e) => e.stopPropagation()}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      readOnly={readOnly}
      className={['ri-input', focused ? 'ri-input--focused' : '', readOnly ? 'ri-input--readonly' : ''].filter(Boolean).join(' ')}
    />
  );
};

/* ── LabeledField ─────────────────────────────────────────────────────────────
   Wraps any input with:
   • a visible micro-label above it
   • a red * badge if required
   • an (i) tooltip icon showing description on hover
   • for date inputs: shows a grey "placeholder" text via a sibling span when empty
───────────────────────────────────────────────────────────────────────────── */
const LabeledField = ({ label, required = false, description, children, type, value }) => {
  const [tipVisible, setTipVisible] = useState(false);
  const isDateEmpty = type === 'date' && (!value || value === '');

  return (
    <div className="ri-lf" style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Label row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, lineHeight: 1 }}>
        <span style={{
          fontSize: '0.65rem', fontWeight: 700, color: '#475569',
          textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
        }}>
          {label}
        </span>
        {required && (
          <span style={{
            fontSize: '0.6rem', fontWeight: 700, color: '#EF4444',
            background: '#FEF2F2', border: '1px solid #FECACA',
            borderRadius: 3, padding: '0px 4px', lineHeight: '1.4',
          }}>
            Required
          </span>
        )}
        {description && (
          <span
            onMouseEnter={() => setTipVisible(true)}
            onMouseLeave={() => setTipVisible(false)}
            style={{ cursor: 'help', display: 'flex', alignItems: 'center', position: 'relative' }}
          >
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
              <circle cx="8" cy="8" r="7.5" stroke="#94A3B8" strokeWidth="1.2" fill="#F1F5F9" />
              <text x="8" y="12" textAnchor="middle" fontSize="10" fontWeight="700" fill="#64748B">i</text>
            </svg>
            {tipVisible && (
              <div style={{
                position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                marginBottom: 6, zIndex: 99999,
                background: '#1E293B', color: '#F1F5F9',
                fontSize: '0.7rem', lineHeight: 1.5, fontWeight: 500,
                padding: '6px 10px', borderRadius: 7,
                boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
                whiteSpace: 'nowrap', maxWidth: 220, textAlign: 'center',
                pointerEvents: 'none',
              }}>
                {description}
                <div style={{
                  position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                  width: 0, height: 0,
                  borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
                  borderTop: '5px solid #1E293B',
                }} />
              </div>
            )}
          </span>
        )}
      </div>

      {/* Input + date placeholder overlay */}
      <div style={{ position: 'relative' }}>
        {children}
        {type === 'date' && isDateEmpty && (
          <span style={{
            position: 'absolute', top: '50%', left: 10, transform: 'translateY(-50%)',
            fontSize: '0.72rem', color: '#94A3B8', pointerEvents: 'none',
            fontStyle: 'italic',
          }}>
            {label}
          </span>
        )}
      </div>
    </div>
  );
};

const emptyItem = () => ({
  _id: null, _isNew: true,
  name: '',
  requestsSupported: [],
  make: '',
  modelNo: '',
  sku: [],
  costPerUnit: '',
  quantity: { active: '', min: '', max: '' },
  locationDetails: { room: '', notes: '' },
  warrantyDetails: [{ boughtOn: '', boughtFrom: '', warrantyExpirationDate: '' }],
  images: [],
  invoices: [],
});

const FileUploadPanel = ({ propertyId, images, invoices, onImagesChange, onInvoicesChange }) => {
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const imageInputRef = useRef(null);
  const invoiceInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const result = await uploadRecurringInventoryInvoice(file, propertyId);
      if (result?.fileUrl) { onImagesChange([...images, { url: result.fileUrl }]); toast.success('Image uploaded'); }
    } catch { toast.error('Image upload failed'); }
    finally { setUploadingImage(false); e.target.value = ''; }
  };

  const handleInvoiceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingInvoice(true);
    try {
      const result = await uploadRecurringInventoryInvoice(file, propertyId);
      if (result?.fileUrl) { onInvoicesChange([...invoices, { url: result.fileUrl }]); toast.success('Invoice uploaded'); }
    } catch { toast.error('Invoice upload failed'); }
    finally { setUploadingInvoice(false); e.target.value = ''; }
  };

  const removeImage = (idx) => onImagesChange(images.filter((_, i) => i !== idx));
  const removeInvoice = (idx) => onInvoicesChange(invoices.filter((_, i) => i !== idx));

  return (
    <div className="ri-upload-panel" onClick={(e) => e.stopPropagation()}>
      <div className="ri-upload-section">
        <div className="ri-upload-section__header">
          <FeatherIcon icon="image" size={12} color="#0D9488" /><span>Images</span>
          <span className="ri-upload-count">{images.length}</span>
        </div>
        <div className="ri-upload-file-list">
          {images.map((img, i) => (
            <div key={i} className="ri-upload-chip">
              <a href={img.url} target="_blank" rel="noreferrer" className="ri-upload-chip__name"><FeatherIcon icon="image" size={10} /> Image {i + 1}</a>
              <button type="button" className="ri-upload-chip__remove" onClick={() => removeImage(i)}><FeatherIcon icon="x" size={9} /></button>
            </div>
          ))}
        </div>
        <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
        <button type="button" className="ri-upload-btn" disabled={uploadingImage || !propertyId} onClick={() => imageInputRef.current?.click()}>
          {uploadingImage ? <Spinner size="sm" /> : <FeatherIcon icon="upload" size={11} />}
          {uploadingImage ? 'Uploading…' : 'Add Image'}
        </button>
        {!propertyId && <span className="ri-upload-hint">Select a property first</span>}
      </div>

      <div className="ri-upload-section">
        <div className="ri-upload-section__header">
          <FeatherIcon icon="file-text" size={12} color="#7C3AED" /><span>Invoices</span>
          <span className="ri-upload-count">{invoices.length}</span>
        </div>
        <div className="ri-upload-file-list">
          {invoices.map((inv, i) => (
            <div key={i} className="ri-upload-chip ri-upload-chip--invoice">
              <a href={inv.url} target="_blank" rel="noreferrer" className="ri-upload-chip__name"><FeatherIcon icon="file-text" size={10} /> Invoice {i + 1}</a>
              <button type="button" className="ri-upload-chip__remove" onClick={() => removeInvoice(i)}><FeatherIcon icon="x" size={9} /></button>
            </div>
          ))}
        </div>
        <input ref={invoiceInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }} onChange={handleInvoiceUpload} />
        <button type="button" className="ri-upload-btn ri-upload-btn--invoice" disabled={uploadingInvoice || !propertyId} onClick={() => invoiceInputRef.current?.click()}>
          {uploadingInvoice ? <Spinner size="sm" /> : <FeatherIcon icon="upload" size={11} />}
          {uploadingInvoice ? 'Uploading…' : 'Add Invoice'}
        </button>
        {!propertyId && <span className="ri-upload-hint">Select a property first</span>}
      </div>
    </div>
  );
};

const EditableRow = ({ rowData: init, columns, colWidths, onSave, onCancel, isNew, requestTypeOptions, propertyId }) => {
  const [data, setData] = useState({ ...init });
  const [saving, setSaving] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const selectStyles = buildSelectStyles();

  const set = (k, v) => setData((p) => ({ ...p, [k]: v }));
  const setNested = (parent, k, v) => setData((p) => ({ ...p, [parent]: { ...(p[parent] || {}), [k]: v } }));
  const setWarranty = (k, v) => setData((p) => ({ ...p, warrantyDetails: [{ ...(p.warrantyDetails?.[0] || {}), [k]: v }] }));

  const save = async () => {
    if (!data.name?.trim()) {
      Swal.fire({ title: 'Validation Error', text: 'Item name is required.', icon: 'error', confirmButtonColor: '#DC2626' });
      return;
    }
    if (data.quantity?.active === '' || data.quantity?.active === undefined) {
      Swal.fire({ title: 'Validation Error', text: 'Active quantity is required.', icon: 'error', confirmButtonColor: '#DC2626' });
      return;
    }
    setSaving(true);
    try { await onSave(data); } catch { } finally { setSaving(false); }
  };

  const skuValue = Array.isArray(data.sku) ? data.sku.join(', ') : '';
  const handleSkuChange = (v) => set('sku', v ? v.split(',').map((s) => s.trim()).filter(Boolean) : []);
  const warrantyEntry = data.warrantyDetails?.[0] || {};
  const reqVal = (data.requestsSupported || []).map((id) => {
    const raw = id?._id || id;
    return requestTypeOptions.find((o) => o.value === raw) || { value: raw, label: String(raw).slice(-6) };
  });
  const totalFiles = (data.images?.length || 0) + (data.invoices?.length || 0);

  const renderCell = (col) => {
    const k = col.key;
    const w = colWidths[k] || FACTORY_WIDTHS[k] || 110;
    const cls = `ri-editable-td ${isNew ? 'ri-editable-td--new' : 'ri-editable-td--edit'}`;
    const sty = { width: w, minWidth: w };

    if (k === 'expand') return (
      <td key="expand" className={cls} style={sty}>
        <div className={`ri-expand-icon ${isNew ? 'ri-expand-icon--new' : 'ri-expand-icon--edit'}`}>
          <FeatherIcon icon={isNew ? 'plus' : 'edit-2'} size={12} color={isNew ? '#0D9488' : '#1D4ED8'} />
        </div>
      </td>
    );

    if (k === 'actions') return (
      <td key="actions" className={`${cls} ri-editable-td--nowrap`} style={sty}>
        <div className="ri-action-group">
          <Tooltip title={`Files (${totalFiles})`} arrow>
            <button type="button" className={`ri-ebtn ri-ebtn--upload${showUpload ? ' active' : ''}`}
              onClick={(e) => { e.stopPropagation(); setShowUpload((v) => !v); }}>
              <FeatherIcon icon="paperclip" size={13} color="#7C3AED" />
              {totalFiles > 0 && <span className="ri-file-count">{totalFiles}</span>}
            </button>
          </Tooltip>
          <button type="button" onClick={save} disabled={saving} className={`ri-btn ri-btn--save${saving ? ' ri-btn--saving' : ''}`}>
            {saving ? <Spinner size="sm" className="ri-spinner--sm" /> : <FeatherIcon icon="check" size={13} />}
            {isNew ? 'Create' : 'Save'}
          </button>
          <button type="button" onClick={onCancel} disabled={saving} className="ri-btn ri-btn--cancel">
            <FeatherIcon icon="x" size={13} /> Cancel
          </button>
        </div>
      </td>
    );

    if (k === 'name') return (
      <td key={k} className={cls} style={sty}>
        <LabeledField label="Item Name" required description="Full descriptive name of this inventory item (e.g. Fire Extinguisher, HVAC Filter)">
          <FInput value={data.name} onChange={(v) => set('name', v)} placeholder="e.g. Fire Extinguisher" />
        </LabeledField>
      </td>
    );
    if (k === 'requestsSupported') return (
      <td key={k} className={cls} style={sty} onClick={(e) => e.stopPropagation()}>
        <LabeledField label="Request Types" required description="Which maintenance or service request types this item is used for">
          <Select isMulti options={requestTypeOptions} value={reqVal}
            onChange={(opts) => set('requestsSupported', (opts || []).map((o) => o.value))}
            menuPortalTarget={document.body} styles={selectStyles} placeholder="Select types…" />
        </LabeledField>
      </td>
    );
    if (k === 'make') return (
      <td key={k} className={cls} style={sty}>
        <LabeledField label="Make / Brand" description="Manufacturer or brand name (e.g. Honeywell, 3M, Bosch)">
          <FInput value={data.make} onChange={(v) => set('make', v)} placeholder="e.g. Honeywell" />
        </LabeledField>
      </td>
    );
    if (k === 'modelNo') return (
      <td key={k} className={cls} style={sty}>
        <LabeledField label="Model No." description="Model number or part number as printed on the product label">
          <FInput value={data.modelNo} onChange={(v) => set('modelNo', v)} placeholder="e.g. HW-2200X" />
        </LabeledField>
      </td>
    );
    if (k === 'sku') return (
      <td key={k} className={cls} style={sty}>
        <LabeledField label="SKU(s)" description="Stock-keeping unit codes — enter multiple separated by commas">
          <FInput value={skuValue} onChange={handleSkuChange} placeholder="e.g. SKU-001, SKU-002" />
        </LabeledField>
      </td>
    );
    if (k === 'costPerUnit') return (
      <td key={k} className={cls} style={sty}>
        <LabeledField label="Cost / Unit (CAD)" required description="Purchase price per single unit in Canadian dollars">
          <FInput value={data.costPerUnit} onChange={(v) => set('costPerUnit', v)} type="number" min="0" step="0.01" placeholder="0.00" />
        </LabeledField>
      </td>
    );
    if (k === 'qty_active') return (
      <td key={k} className={cls} style={sty}>
        <LabeledField label="Active Qty" required description="Current number of units available for use right now">
          <FInput value={data.quantity?.active} onChange={(v) => setNested('quantity', 'active', v)} type="number" min="0" placeholder="0" />
        </LabeledField>
      </td>
    );
    if (k === 'qty_min') return (
      <td key={k} className={cls} style={sty}>
        <LabeledField label="Min Qty" description="Low-stock alert triggers when active quantity falls to or below this value">
          <FInput value={data.quantity?.min} onChange={(v) => setNested('quantity', 'min', v)} type="number" min="0" placeholder="0" />
        </LabeledField>
      </td>
    );
    if (k === 'qty_max') return (
      <td key={k} className={cls} style={sty}>
        <LabeledField label="Max Qty" description="Maximum stocking level for reorder planning — must be ≥ Min Qty">
          <FInput value={data.quantity?.max} onChange={(v) => setNested('quantity', 'max', v)} type="number" min="0" placeholder="0" />
        </LabeledField>
      </td>
    );
    if (k === 'location_room') return (
      <td key={k} className={cls} style={sty}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <LabeledField label="Room / Area" description="Room number or area name where this item is physically stored (e.g. Boiler Room, Storage 2B)">
            <FInput value={data.locationDetails?.room} onChange={(v) => setNested('locationDetails', 'room', v)} placeholder="e.g. Boiler Room" />
          </LabeledField>
          <LabeledField label="Location Notes" description="Additional directions about exact storage spot (e.g. Top shelf, left cabinet)">
            <FInput value={data.locationDetails?.notes} onChange={(v) => setNested('locationDetails', 'notes', v)} placeholder="e.g. Top shelf, left cabinet" />
          </LabeledField>
        </div>
      </td>
    );
    if (k === 'warrantyExpiry') return (
      <td key={k} className={cls} style={sty}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <LabeledField label="Purchase Date" type="date" value={warrantyEntry.boughtOn || ''} description="Date this item was purchased — cannot be set to a future date">
            <FInput value={warrantyEntry.boughtOn || ''} type="date" onChange={(v) => setWarranty('boughtOn', v)} />
          </LabeledField>
          <LabeledField label="Bought From" required description="Supplier or vendor this item was purchased from (e.g. Home Depot, Amazon)">
            <FInput value={warrantyEntry.boughtFrom || ''} type="text" onChange={(v) => setWarranty('boughtFrom', v)} placeholder="e.g. Home Depot" />
          </LabeledField>
          <LabeledField label="Warranty Expiry" type="date" value={warrantyEntry.warrantyExpirationDate || ''} description="Date the manufacturer warranty expires — must be on or after the purchase date">
            <FInput value={warrantyEntry.warrantyExpirationDate || ''} type="date" onChange={(v) => setWarranty('warrantyExpirationDate', v)} />
          </LabeledField>
        </div>
      </td>
    );

    return <td key={k} className={cls} style={sty}><span className="ri-cell-muted">—</span></td>;
  };

  return (
    <>
      <tr className={`ri-editable-row ${isNew ? 'ri-editable-row--new' : 'ri-editable-row--edit'}`}>
        {columns.map((col) => renderCell(col))}
      </tr>
      {showUpload && (
        <tr className="ri-upload-row">
          <td colSpan={columns.length} className="ri-upload-row__td">
            <FileUploadPanel
              propertyId={propertyId}
              images={data.images || []}
              invoices={data.invoices || []}
              onImagesChange={(v) => set('images', v)}
              onInvoicesChange={(v) => set('invoices', v)}
            />
          </td>
        </tr>
      )}
    </>
  );
};

const ExpandedDetailPanel = ({ item, requestTypeOptions, colCount }) => {
  const warranties = item.warrantyDetails || [];
  const images = (item.images || []).filter((i) => i.url);
  const invoices = (item.invoices || []).filter((i) => i.url);
  const reqLabels = (item.requestsSupported || []).map((id) => {
    const raw = id?._id || id;
    return requestTypeOptions.find((o) => o.value === raw)?.label || String(raw).slice(-6);
  });

  return (
    <tr className="ri-expanded-row">
      <td colSpan={colCount} className="ri-expanded-td">
        <div className="ri-detail-panel">
          <div className="ri-detail-panel__grid">

            <div className="ri-detail-section">
              <div className="ri-detail-section__label"><FeatherIcon icon="info" size={11} /> Item Info</div>
              <div className="ri-detail-kv"><span>Name</span>      <strong>{cap(item.name)}</strong></div>
              <div className="ri-detail-kv"><span>Make</span>      <strong>{cap(item.make) || '—'}</strong></div>
              <div className="ri-detail-kv"><span>Model No.</span> <strong>{cap(item.modelNo) || '—'}</strong></div>
              <div className="ri-detail-kv"><span>Cost / Unit</span><strong className="ri-detail-cost">{fmtCurrency(item.costPerUnit)}</strong></div>
            </div>

            <div className="ri-detail-section">
              <div className="ri-detail-section__label"><FeatherIcon icon="package" size={11} /> Quantity &amp; Location</div>
              <div className="ri-detail-kv"><span>Active Qty</span><strong>{item.quantity?.active ?? '—'}</strong></div>
              <div className="ri-detail-kv"><span>Min Qty</span>   <strong>{item.quantity?.min ?? '—'}</strong></div>
              <div className="ri-detail-kv"><span>Max Qty</span>   <strong>{item.quantity?.max ?? '—'}</strong></div>
              <div className="ri-detail-kv"><span>Room</span>      <strong>{cap(item.locationDetails?.room) || '—'}</strong></div>
              <div className="ri-detail-kv"><span>Notes</span>     <strong>{cap(item.locationDetails?.notes) || '—'}</strong></div>
            </div>

            <div className="ri-detail-section">
              <div className="ri-detail-section__label"><FeatherIcon icon="shield" size={11} /> Warranty Details</div>
              {warranties.length === 0 && <span className="ri-cell-muted" style={{ fontSize: '0.75rem' }}>No warranty recorded</span>}
              {warranties.map((w, i) => (
                <div key={i} className="ri-warranty-entry">
                  <div className="ri-detail-kv"><span>Bought On</span>   <strong>{fmtDate(w.boughtOn)}</strong></div>
                  <div className="ri-detail-kv"><span>Bought From</span> <strong>{cap(w.boughtFrom) || '—'}</strong></div>
                  <div className="ri-detail-kv"><span>Expires</span>     <strong>{fmtDate(w.warrantyExpirationDate)}</strong></div>
                </div>
              ))}
            </div>

            <div className="ri-detail-section">
              <div className="ri-detail-section__label"><FeatherIcon icon="tag" size={11} /> SKU &amp; Requests</div>
              <div className="ri-detail-kv"><span>SKU(s)</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {(item.sku || []).length
                    ? item.sku.map((s, i) => <span key={i} className="ri-sku-chip">{s}</span>)
                    : <span className="ri-cell-muted">—</span>}
                </div>
              </div>
              <div className="ri-detail-kv" style={{ alignItems: 'flex-start' }}><span>Requests</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {reqLabels.length
                    ? reqLabels.map((l, i) => <span key={i} className="ri-req-chip">{l}</span>)
                    : <span className="ri-cell-muted">—</span>}
                </div>
              </div>
              {(images.length > 0 || invoices.length > 0) && (
                <>
                  <div className="ri-detail-section__label" style={{ marginTop: 10 }}><FeatherIcon icon="paperclip" size={11} /> Files</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {images.map((img, i) => <a key={i} href={img.url} target="_blank" rel="noreferrer" className="ri-file-chip"><FeatherIcon icon="image" size={10} /> Image {i + 1}</a>)}
                    {invoices.map((inv, i) => <a key={i} href={inv.url} target="_blank" rel="noreferrer" className="ri-file-chip ri-file-chip--invoice"><FeatherIcon icon="file-text" size={10} /> Invoice {i + 1}</a>)}
                  </div>
                </>
              )}
            </div>

          </div>
        </div>
      </td>
    </tr>
  );
};

const ColWidthSettingsPanel = ({ colWidths, onApply, onClose }) => {
  const [draft, setDraft] = useState({ ...colWidths });
  const set = (k, v) => {
    const n = parseInt(v, 10);
    setDraft((p) => ({ ...p, [k]: isNaN(n) ? p[k] : Math.max(40, Math.min(600, n)) }));
  };
  const groups = [
    { label: 'Identity', keys: ['expand', 'name', 'requestsSupported', 'make', 'modelNo', 'sku'] },
    { label: 'Stock & Cost', keys: ['costPerUnit', 'qty_active', 'qty_min', 'qty_max'] },
    { label: 'Context', keys: ['location_room', 'warrantyExpiry', 'actions'] },
  ];
  return (
    <div className="ri-settings-panel">
      <div className="ri-settings-panel__header">
        <div className="ri-settings-panel__header-left">
          <div className="ri-settings-panel__header-icon"><FeatherIcon icon="sliders" size={14} color="#93C5FD" /></div>
          <div>
            <div className="ri-settings-panel__header-label">Column Settings</div>
            <div className="ri-settings-panel__header-title">Adjust Default Widths</div>
          </div>
        </div>
        <button className="ri-icon-btn" onClick={onClose} type="button"><FeatherIcon icon="x" size={14} color="white" /></button>
      </div>
      <div className="ri-settings-panel__body">
        <div className="ri-settings-panel__info-banner">
          <FeatherIcon icon="info" size={12} color="#1D4ED8" /> Drag column handles for one-time adjustments. Save values here as persistent defaults.
        </div>
        {groups.map((g) => (
          <div key={g.label} className="ri-settings-panel__group">
            <div className="ri-settings-panel__group-label">{g.label}</div>
            <div className="ri-settings-panel__col-grid">
              {g.keys.map((k) => (
                <div key={k} className="ri-settings-panel__col-row">
                  <span className="ri-settings-panel__col-label">{COL_LABELS[k] || k}</span>
                  <div className="ri-settings-panel__stepper">
                    <button type="button" className="ri-settings-panel__stepper-btn" onClick={() => set(k, (draft[k] || FACTORY_WIDTHS[k]) - 10)}>−</button>
                    <input type="number" className="ri-settings-panel__stepper-input" value={draft[k] || FACTORY_WIDTHS[k]} onChange={(e) => set(k, e.target.value)} min={40} max={600} />
                    <button type="button" className="ri-settings-panel__stepper-btn" onClick={() => set(k, (draft[k] || FACTORY_WIDTHS[k]) + 10)}>+</button>
                    <span className="ri-settings-panel__stepper-unit">px</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="ri-settings-panel__footer">
        <button type="button" className="ri-btn ri-btn--ghost" onClick={() => setDraft({ ...FACTORY_WIDTHS })}>
          <FeatherIcon icon="rotate-ccw" size={12} /> Reset
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="ri-btn ri-btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="ri-btn ri-btn--primary" onClick={() => { onApply(draft); onClose(); }}>
            <FeatherIcon icon="save" size={13} /> Save &amp; Apply
          </button>
        </div>
      </div>
    </div>
  );
};

const cellVal = (row, key, requestTypeOptions) => {
  switch (key) {
    case 'name': return <span className="ri-cell-name">{cap(row.name)}</span>;
    case 'requestsSupported': return <RequestsBadge ids={row.requestsSupported} requestTypeOptions={requestTypeOptions} />;
    case 'make': return row.make ? <span className="ri-cell-text">{cap(row.make)}</span> : <span className="ri-cell-muted">—</span>;
    case 'modelNo': return row.modelNo ? <span className="ri-cell-mono">{row.modelNo}</span> : <span className="ri-cell-muted">—</span>;
    case 'sku': return <SkuBadge sku={row.sku} />;
    case 'costPerUnit': return <span className="ri-cell-cost">{fmtCurrency(row.costPerUnit)}</span>;
    case 'qty_active': {
      const q = row.quantity?.active;
      const min = Number(row.quantity?.min);
      const isLow = min > 0 && Number(q) <= min;
      return q !== undefined && q !== ''
        ? <span className={`ri-qty-badge ${isLow ? 'ri-qty-badge--low' : 'ri-qty-badge--ok'}`}>{q}</span>
        : <span className="ri-cell-muted">—</span>;
    }
    case 'qty_min': return row.quantity?.min !== undefined && row.quantity?.min !== '' ? <span className="ri-cell-number">{row.quantity.min}</span> : <span className="ri-cell-muted">—</span>;
    case 'qty_max': return row.quantity?.max !== undefined && row.quantity?.max !== '' ? <span className="ri-cell-number">{row.quantity.max}</span> : <span className="ri-cell-muted">—</span>;
    case 'location_room': return row.locationDetails?.room ? <span className="ri-cell-text">{cap(row.locationDetails.room)}</span> : <span className="ri-cell-muted">—</span>;
    case 'warrantyExpiry': return <WarrantyBadge item={row} />;
    default: return <span className="ri-cell-text">{row[key] ?? '—'}</span>;
  }
};

const buildConfirmHtml = (data, isNew) => {
  const kv = (icon, label, val) =>
    `<div style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:6px 10px;border-radius:7px;background:#F8FAFC;margin-bottom:3px"><div style="display:flex;align-items:center;gap:6px"><span>${icon}</span><span style="color:#64748B;font-size:10.5px;font-weight:600;text-transform:uppercase">${label}</span></div><div>${val}</div></div>`;
  const warranty = data.warrantyDetails?.[0] || {};
  const warrantyDate = warranty.warrantyExpirationDate;
  const boughtOn = warranty.boughtOn;
  const boughtFrom = warranty.boughtFrom;
  const imgCount = data.images?.length || 0;
  const invCount = data.invoices?.length || 0;
  return `
    <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;text-align:left">
      <div style="background:linear-gradient(135deg,#EFF6FF,#F0FDF4);border:1px solid #DBEAFE;border-radius:10px;padding:12px 14px;margin-bottom:14px;display:flex;align-items:center;gap:12px">
        <div style="width:40px;height:40px;background:linear-gradient(135deg,#1D4ED8,#0D9488);border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:20px">📦</div>
        <div>
          <div style="font-size:10px;color:#64748B;font-weight:600;letter-spacing:0.6px;text-transform:uppercase">${isNew ? 'New Inventory Item' : 'Update Item'}</div>
          <div style="font-size:15px;font-weight:700;color:#0F172A">${data.name || 'Unnamed Item'}</div>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px">
        <div>
          ${kv('🏷️', 'Make', `<span style="font-weight:600;font-size:12px">${data.make || '—'}</span>`)}
          ${kv('🔢', 'Model No.', `<span style="font-family:monospace;font-size:12px;font-weight:600">${data.modelNo || '—'}</span>`)}
          ${kv('📍', 'Room', `<span style="font-weight:600;font-size:12px">${data.locationDetails?.room || '—'}</span>`)}
          ${kv('💰', 'Cost/Unit', `<span style="font-weight:700;font-size:12px;color:#059669">${data.costPerUnit ? '$' + Number(data.costPerUnit).toFixed(2) : '—'}</span>`)}
        </div>
        <div>
          ${kv('📦', 'Active Qty', `<span style="font-weight:700;font-size:12px">${data.quantity?.active ?? '—'}</span>`)}
          ${kv('⬇️', 'Min / Max', `<span style="font-weight:600;font-size:12px">${data.quantity?.min ?? '—'} / ${data.quantity?.max ?? '—'}</span>`)}
          ${kv('🔖', 'SKU(s)', `<span style="font-size:11px">${(data.sku || []).join(', ') || '—'}</span>`)}
          ${kv('🏪', 'Bought From', `<span style="font-weight:600;font-size:12px">${boughtFrom || '—'}</span>`)}
        </div>
      </div>
      ${(warrantyDate || boughtOn) ? `
        <div style="margin-top:8px;padding:8px 12px;background:#F0FDF4;border-radius:8px;border:1px solid #BBF7D0;font-size:12px;color:#065F46;display:flex;gap:14px;flex-wrap:wrap">
          ${boughtOn ? `<span>🗓️ Bought: ${new Date(boughtOn).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}</span>` : ''}
          ${warrantyDate ? `<span>🛡️ Warranty: ${new Date(warrantyDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}</span>` : ''}
        </div>` : ''}
      ${(imgCount > 0 || invCount > 0) ? `
        <div style="margin-top:10px;padding:8px 12px;background:#F5F3FF;border-radius:8px;border:1px solid #DDD6FE;font-size:12px;color:#5B21B6;display:flex;gap:14px">
          ${imgCount > 0 ? `<span>🖼️ ${imgCount} image${imgCount !== 1 ? 's' : ''}</span>` : ''}
          ${invCount > 0 ? `<span>📄 ${invCount} invoice${invCount !== 1 ? 's' : ''}</span>` : ''}
        </div>` : ''}
    </div>`;
};

const MoveItemModal = ({ item, propertyList, onConfirm, onClose, moving }) => {
  const [destPropertyId, setDestPropertyId] = useState('');
  const [destBuildingName, setDestBuildingName] = useState('');
  const [qty, setQty] = useState(item?.quantity?.active || 0);
  const [error, setError] = useState('');

  const maxQty = item?.quantity?.active || 0;

  const buildingOpts = useMemo(() => {
    const prop = propertyList.find((p) => p._id === destPropertyId);
    return (prop?.building || []).filter((b) => b?.buildingName).map((b) => b.buildingName);
  }, [destPropertyId, propertyList]);

  const destPropertyLabel = useMemo(() => {
    const p = propertyList.find((x) => x._id === destPropertyId);
    return p ? `${p.title}${p.location ? `, ${p.location}` : ''}` : '';
  }, [destPropertyId, propertyList]);

  const isPartial = qty < maxQty;
  const pct = maxQty > 0 ? Math.round((qty / maxQty) * 100) : 0;

  const handlePropertyChange = (pid) => {
    setDestPropertyId(pid);
    setDestBuildingName('');
    setError('');
  };

  const handleQtyChange = (v) => {
    const n = Math.max(1, Math.min(maxQty, Number(v) || 1));
    setQty(n);
    setError('');
  };

  const handleConfirm = () => {
    if (!destPropertyId) { setError('Please select a destination property.'); return; }
    if (!destBuildingName) { setError('Please select a destination building.'); return; }
    if (qty < 1 || qty > maxQty) { setError(`Quantity must be between 1 and ${maxQty}.`); return; }
    onConfirm({ item_id: item._id, destination_property_id: destPropertyId, destination_building_name: destBuildingName, quantity: qty });
  };

  useEffect(() => {
    if (buildingOpts.length === 1) setDestBuildingName(buildingOpts[0]);
    else setDestBuildingName('');
  }, [buildingOpts]);

  const selectStyle = (hasVal) => ({
    width: '100%', padding: '9px 36px 9px 12px', border: `1.5px solid ${hasVal ? '#1D4ED8' : '#CBD5E1'}`,
    borderRadius: 9, fontSize: '0.82rem', color: '#0F172A', background: '#fff',
    outline: 'none', cursor: 'pointer', appearance: 'none',
    backgroundImage: "url('data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E')",
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
    boxShadow: hasVal ? '0 0 0 3px #DBEAFE' : 'none', transition: 'all 0.12s',
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99998,
      background: 'rgba(15,28,46,0.55)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520,
        boxShadow: '0 24px 64px rgba(15,28,46,0.24)', overflow: 'hidden',
        animation: 'ri-modal-in 0.18s ease',
      }}>
        <style>{`@keyframes ri-modal-in{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}`}</style>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg,#0F1C2E 0%,#1A2F4B 100%)',
          padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{
            width: 42, height: 42, borderRadius: 11, flexShrink: 0,
            background: 'rgba(13,148,136,0.22)', border: '1px solid rgba(13,148,136,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FeatherIcon icon="move" size={18} color="#5EEAD4" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(94,234,212,0.6)', marginBottom: 3 }}>Move Inventory Item</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#F0F9FF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item?.name}</div>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8,
            width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <FeatherIcon icon="x" size={15} color="rgba(255,255,255,0.7)" />
          </button>
        </div>

        {/* Item summary strip */}
        <div style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', padding: '10px 22px', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          {[
            { icon: 'tag', label: 'Make', value: item?.make || '—' },
            { icon: 'hash', label: 'Model', value: item?.modelNo || '—' },
            { icon: 'package', label: 'Available Qty', value: maxQty, bold: true, color: '#059669' },
            { icon: 'dollar-sign', label: 'Cost/Unit', value: item?.costPerUnit ? `$${Number(item.costPerUnit).toFixed(2)}` : '—' },
          ].map(({ icon, label, value, bold, color }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FeatherIcon icon={icon} size={11} color="#94A3B8" />
              <span style={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
              <span style={{ fontSize: '0.78rem', fontWeight: bold ? 800 : 600, color: color || '#0F172A', marginLeft: 2 }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Form body */}
        <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Destination property */}
          <div>
            <div style={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#475569', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
              <FeatherIcon icon="home" size={11} color="#1D4ED8" /> Destination Property
              <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#EF4444', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 3, padding: '0 4px' }}>Required</span>
            </div>
            <select value={destPropertyId} onChange={(e) => handlePropertyChange(e.target.value)} style={selectStyle(!!destPropertyId)}>
              <option value="">Select a property…</option>
              {propertyList.map((p) => <option key={p._id} value={p._id}>{p.title}{p.location ? `, ${p.location}` : ''}</option>)}
            </select>
          </div>

          {/* Destination building */}
          <div>
            <div style={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#475569', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
              <FeatherIcon icon="layers" size={11} color="#1D4ED8" /> Destination Building
              <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#EF4444', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 3, padding: '0 4px' }}>Required</span>
            </div>
            <select value={destBuildingName} onChange={(e) => { setDestBuildingName(e.target.value); setError(''); }}
              disabled={!destPropertyId || buildingOpts.length === 0}
              style={{ ...selectStyle(!!destBuildingName), background: (!destPropertyId || buildingOpts.length === 0) ? '#F8FAFC' : '#fff', opacity: (!destPropertyId || buildingOpts.length === 0) ? 0.55 : 1, cursor: destPropertyId ? 'pointer' : 'not-allowed' }}>
              <option value="">{!destPropertyId ? 'Select a property first…' : buildingOpts.length === 0 ? 'No buildings available' : 'Select a building…'}</option>
              {buildingOpts.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          {/* Quantity */}
          <div>
            <div style={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#475569', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
              <FeatherIcon icon="sliders" size={11} color="#1D4ED8" /> Quantity to Move
              <span style={{ marginLeft: 'auto', fontSize: '0.71rem', fontWeight: 700, color: isPartial ? '#D97706' : '#059669' }}>
                {qty} of {maxQty} units ({pct}%)
              </span>
            </div>
            <input type="range" min={1} max={Math.max(1, maxQty)} value={qty}
              onChange={(e) => handleQtyChange(e.target.value)}
              style={{ width: '100%', accentColor: isPartial ? '#D97706' : '#1D4ED8', marginBottom: 8, cursor: 'pointer' }}
            />
            <div style={{ height: 6, borderRadius: 99, background: '#E2E8F0', marginBottom: 10, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 99, width: `${pct}%`, transition: 'width 0.15s ease',
                background: isPartial ? 'linear-gradient(90deg,#F59E0B,#D97706)' : 'linear-gradient(90deg,#10B981,#059669)'
              }} />
            </div>
            {/* Quick-set chips */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {[{ label: 'All', value: maxQty }, { label: '75%', value: Math.max(1, Math.floor(maxQty * 0.75)) },
              { label: '50%', value: Math.max(1, Math.floor(maxQty * 0.5)) }, { label: '25%', value: Math.max(1, Math.floor(maxQty * 0.25)) },
              { label: '1', value: 1 }]
                .filter((x, i, arr) => arr.findIndex(y => y.value === x.value) === i)
                .map(({ label, value }) => (
                  <button key={label} type="button" onClick={() => handleQtyChange(value)} style={{
                    padding: '3px 10px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
                    border: qty === value ? '1.5px solid #1D4ED8' : '1.5px solid #CBD5E1',
                    background: qty === value ? '#EFF6FF' : '#F8FAFC',
                    color: qty === value ? '#1D4ED8' : '#475569', transition: 'all 0.1s',
                  }}>{label}</button>
                ))
              }
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                <span style={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 600 }}>Custom:</span>
                <input type="number" min={1} max={maxQty} value={qty} onChange={(e) => handleQtyChange(e.target.value)}
                  style={{ width: 64, height: 28, border: '1.5px solid #CBD5E1', borderRadius: 7, padding: '0 8px', fontSize: '0.78rem', fontWeight: 700, color: '#0F172A', outline: 'none', textAlign: 'center' }}
                />
              </div>
            </div>
          </div>

          {/* Move-type indicator */}
          <div style={{
            borderRadius: 10, padding: '10px 14px',
            background: isPartial ? '#FFFBEB' : '#ECFDF5',
            border: `1px solid ${isPartial ? '#FDE68A' : '#A7F3D0'}`,
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
            <FeatherIcon icon={isPartial ? 'git-branch' : 'arrow-right-circle'} size={15} color={isPartial ? '#D97706' : '#059669'} />
            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: isPartial ? '#92400E' : '#065F46', marginBottom: 2 }}>
                {isPartial ? 'Partial Move' : 'Full Move'}
              </div>
              <div style={{ fontSize: '0.7rem', color: isPartial ? '#B45309' : '#047857', lineHeight: 1.45 }}>
                {isPartial
                  ? `${qty} unit${qty !== 1 ? 's' : ''} will move to ${destBuildingName || 'the destination'} — ${maxQty - qty} unit${maxQty - qty !== 1 ? 's' : ''} remain here.`
                  : `All ${maxQty} unit${maxQty !== 1 ? 's' : ''} will move to ${destBuildingName || 'the destination'} and this record will be reassigned.`}
              </div>
            </div>
          </div>

          {/* Destination summary */}
          {destPropertyId && destBuildingName && (
            <div style={{ borderRadius: 10, padding: '10px 14px', background: '#EFF6FF', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', gap: 10 }}>
              <FeatherIcon icon="navigation" size={14} color="#1D4ED8" />
              <div style={{ fontSize: '0.75rem', color: '#1E40AF', fontWeight: 600, lineHeight: 1.4 }}>
                Moving to: <strong>{destPropertyLabel}</strong> › <strong>{destBuildingName}</strong>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ borderRadius: 8, padding: '8px 12px', background: '#FEF2F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FeatherIcon icon="alert-circle" size={13} color="#DC2626" />
              <span style={{ fontSize: '0.75rem', color: '#DC2626', fontWeight: 600 }}>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', background: '#F8FAFC', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={onClose} disabled={moving} style={{
            padding: '9px 20px', borderRadius: 9, border: '1.5px solid #CBD5E1',
            background: '#fff', color: '#475569', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
          }}>Cancel</button>
          <button type="button" onClick={handleConfirm} disabled={moving || !destPropertyId || !destBuildingName} style={{
            padding: '9px 22px', borderRadius: 9, border: 'none',
            background: (moving || !destPropertyId || !destBuildingName) ? '#93C5FD' : 'linear-gradient(135deg,#2563EB,#1D4ED8)',
            color: '#fff', fontSize: '0.82rem', fontWeight: 700,
            cursor: (moving || !destPropertyId || !destBuildingName) ? 'not-allowed' : 'pointer',
            boxShadow: (!moving && destPropertyId && destBuildingName) ? '0 4px 14px rgba(29,78,216,0.35)' : 'none',
            display: 'flex', alignItems: 'center', gap: 7,
          }}>
            {moving ? <><Spinner size="sm" style={{ width: 14, height: 14 }} /> Moving…</> : <><FeatherIcon icon="move" size={14} /> Move {qty} Unit{qty !== 1 ? 's' : ''}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const RecurringInventoryTable = ({
  propertyId,
  buildingName,
  requestTypeOptions = [],
  propertyList = [],
  userRole,
  onDelete,
  scrollThreshold = 992,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingRowId, setEditingRowId] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [showNewRow, setShowNewRow] = useState(false);
  const [colWidths, setColWidths] = useState(loadSavedWidths);
  const [showSettings, setShowSettings] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [moveModal, setMoveModal] = useState({ open: false, item: null });
  const [moving, setMoving] = useState(false);

  const PER_PAGE = 10;
  const canEdit = userRole === 'company';

  useEffect(() => {
    const fn = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  useEffect(() => {
    if (!propertyId || !buildingName) return;
    setLoading(true);
    setCurrentPage(1);
    setEditingRowId(null);
    setShowNewRow(false);
    setExpandedRowId(null);
    getRecurringInventoryItems(propertyId, buildingName)
      .then((res) => setItems(res?.items || []))
      .catch(() => toast.error('Failed to load inventory'))
      .finally(() => setLoading(false));
  }, [propertyId, buildingName]);

  useEffect(() => {
    const handler = (e) => {
      if (showSettings && !e.target.closest('.ri-settings-panel') && !e.target.closest('.ri-settings-btn')) {
        setShowSettings(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSettings]);

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return items;
    const q = searchTerm.toLowerCase();
    return items.filter((item) =>
      (item.name || '').toLowerCase().includes(q) ||
      (item.make || '').toLowerCase().includes(q) ||
      (item.modelNo || '').toLowerCase().includes(q) ||
      (item.sku || []).some((s) => s.toLowerCase().includes(q))
    );
  }, [items, searchTerm]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paged = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const totals = useMemo(() => {
    if (!items.length) return null;
    const totalValue = items.reduce((sum, item) => sum + (Number(item.costPerUnit) || 0) * (Number(item.quantity?.active) || 0), 0);
    const lowStock = items.filter((item) => { const min = Number(item.quantity?.min); return min > 0 && Number(item.quantity?.active) <= min; }).length;
    const expiredWarranties = items.filter((item) => { const d = getWarrantyExpiry(item); return d && isExpired(d); }).length;
    return { total: items.length, totalValue, lowStock, expiredWarranties };
  }, [items]);

  const cols = [
    { key: 'expand', label: '' },
    { key: 'name', label: 'Name', sticky: true },
    { key: 'requestsSupported', label: 'Requests Supported' },
    { key: 'make', label: 'Make' },
    { key: 'modelNo', label: 'Model No.' },
    { key: 'sku', label: 'SKU' },
    { key: 'costPerUnit', label: 'Cost/Unit' },
    { key: 'qty_active', label: 'Qty (Active)' },
    { key: 'qty_min', label: 'Min Qty' },
    { key: 'qty_max', label: 'Max Qty' },
    { key: 'location_room', label: 'Room' },
    { key: 'warrantyExpiry', label: 'Warranty' },
    { key: 'actions', label: '' },
  ];

  const cw = (key) => colWidths[key] || FACTORY_WIDTHS[key] || 110;
  const adjustWidth = (key, delta) => setColWidths((prev) => { const next = { ...prev, [key]: Math.max(60, (prev[key] || FACTORY_WIDTHS[key] || 110) + delta) }; saveWidths(next); return next; });
  const applySettings = (nw) => { setColWidths(nw); saveWidths(nw); toast.success('Column widths saved'); };

  const handleSave = useCallback(async (data) => {
    const isNew = data._isNew;
    const confirmed = await Swal.fire({
      title: '', html: buildConfirmHtml(data, isNew),
      showCancelButton: true,
      confirmButtonText: isNew ? '&#10003;&nbsp; Create Item' : '&#10003;&nbsp; Save Changes',
      cancelButtonText: 'Review Again',
      confirmButtonColor: '#1D4ED8', cancelButtonColor: '#64748B',
      width: 580, padding: '20px 20px 16px',
    });
    if (!confirmed.isConfirmed) return;

    Swal.fire({
      html: `<div style="display:flex;flex-direction:column;align-items:center;gap:14px;padding:12px 0;font-family:-apple-system,sans-serif"><div style="width:44px;height:44px;border:3px solid #DBEAFE;border-top-color:#1D4ED8;border-radius:50%;animation:ri-spin 0.75s linear infinite"></div><div style="font-size:14px;font-weight:600;color:#0F172A">${isNew ? 'Creating…' : 'Saving…'}</div><style>@keyframes ri-spin{to{transform:rotate(360deg)}}</style></div>`,
      showConfirmButton: false, allowOutsideClick: false, width: 280, padding: '24px',
    });

    try {
      const { _isNew, _id, ...payload } = data;
      if (isNew) {
        await createRecurringInventoryItems({ property_id: propertyId, building_name: buildingName, items: [payload] });
      } else {
        await updateRecurringInventoryItem({ item_id: _id, item: payload });
      }
      const refreshed = await getRecurringInventoryItems(propertyId, buildingName);
      setItems(refreshed?.items || []);
      setEditingRowId(null);
      setShowNewRow(false);
      await Swal.fire({
        html: `<div style="display:flex;flex-direction:column;align-items:center;gap:10px;padding:10px 0;font-family:-apple-system,sans-serif"><div style="width:56px;height:56px;background:linear-gradient(135deg,#DCFCE7,#BBF7D0);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:26px">✓</div><div style="font-size:16px;font-weight:700;color:#0F172A">${isNew ? 'Item Created!' : 'Changes Saved!'}</div></div>`,
        showConfirmButton: false, timer: 1400, width: 280, padding: '24px',
      });
    } catch {
      await Swal.fire({ title: 'Error', text: 'Operation failed. Please try again.', icon: 'error', confirmButtonColor: '#DC2626', width: 360 });
    }
  }, [propertyId, buildingName]);

  const handleDelete = useCallback((item) => { if (onDelete) onDelete(item); }, [onDelete]);

  const handleMove = useCallback((item) => {
    setMoveModal({ open: true, item });
  }, []);

  const handleMoveConfirm = useCallback(async (payload) => {
    setMoving(true);
    try {
      await moveRecurringInventoryItem(payload);
      const refreshed = await getRecurringInventoryItems(propertyId, buildingName);
      setItems(refreshed?.items || []);
      setMoveModal({ open: false, item: null });
      toast.success('Item moved successfully');
    } catch {
      toast.error('Move failed. Please try again.');
    } finally {
      setMoving(false);
    }
  }, [propertyId, buildingName]);

  const handleMoveClose = useCallback(() => {
    if (!moving) setMoveModal({ open: false, item: null });
  }, [moving]);

  const handleExport = () => {
    if (!propertyId || !buildingName) return;
    generateRecurringInventoryReport({ property_id: propertyId, building_name: buildingName });
  };

  const maxH = windowWidth < scrollThreshold ? '62vh' : '68vh';

  return (
    <Paper elevation={0} className="ri-wrap ri-paper">

      <div className="ri-header">
        <div className="ri-header__left">
          <div className="ri-header__icon-wrap"><FeatherIcon icon="archive" size={18} color="#93C5FD" /></div>
          <div className="ri-header__meta">
            <div className="ri-header__label">Recurring Inventory</div>
            <div className="ri-header__title">{propertyId && buildingName ? buildingName : 'Select a Property'}</div>
          </div>
          {totals && (
            <div className="ri-header__badges">
              <span className="ri-header__badge ri-header__badge--records">{totals.total} item{totals.total !== 1 ? 's' : ''}</span>
              <span className="ri-header__badge ri-header__badge--value">
                {new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(totals.totalValue)} total value
              </span>
              {totals.lowStock > 0 && <span className="ri-header__badge ri-header__badge--warn"><FeatherIcon icon="alert-triangle" size={10} /> {totals.lowStock} low stock</span>}
              {totals.expiredWarranties > 0 && <span className="ri-header__badge ri-header__badge--danger"><FeatherIcon icon="shield-off" size={10} /> {totals.expiredWarranties} expired</span>}
            </div>
          )}
        </div>
        <div className="ri-header__actions">
          {propertyId && buildingName && (
            <Tooltip title="Export Excel report" arrow>
              <button type="button" className="ri-settings-btn" onClick={handleExport}>
                <FeatherIcon icon="download" size={13} /> Export
              </button>
            </Tooltip>
          )}
          <Tooltip title="Column width settings" arrow>
            <button type="button" className={`ri-settings-btn${showSettings ? ' active' : ''}`} onClick={() => setShowSettings((v) => !v)}>
              <FeatherIcon icon="sliders" size={13} /> Columns
            </button>
          </Tooltip>
          {canEdit && propertyId && buildingName && (
            <button type="button"
              className={`ri-btn ri-add ${showNewRow ? 'ri-btn--danger' : 'ri-btn--primary-solid'}`}
              onClick={() => { if (showNewRow) { setShowNewRow(false); } else { setShowNewRow(true); setEditingRowId(null); setCurrentPage(1); } }}>
              <FeatherIcon icon={showNewRow ? 'x' : 'plus'} size={14} />{showNewRow ? 'Cancel' : 'Add Item'}
            </button>
          )}
        </div>
      </div>

      {propertyId && buildingName && (
        <div className="ri-filter-bar">
          <div className="ri-search-wrap">
            <FeatherIcon icon="search" size={13} color="#94A3B8" className="ri-search-icon" />
            <input type="text" className="ri-search-input" placeholder="Search by name, make, model, SKU…"
              value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
            {searchTerm && <button type="button" className="ri-search-clear" onClick={() => setSearchTerm('')}><FeatherIcon icon="x" size={12} /></button>}
          </div>
          {searchTerm && (
            <button type="button" className="ri-btn ri-btn--ghost ri-filter-clear"
              onClick={() => { setSearchTerm(''); setCurrentPage(1); }}>
              <FeatherIcon icon="x-circle" size={12} /> Clear
            </button>
          )}
          <span className="ri-filter-count">{filtered.length} of {items.length} items</span>
        </div>
      )}

      {showSettings && <ColWidthSettingsPanel colWidths={colWidths} onApply={applySettings} onClose={() => setShowSettings(false)} />}

      <Box sx={{ maxHeight: maxH, overflowY: 'auto', overflowX: 'auto', borderRadius: '0 0 10px 10px' }} className="ri-scroll-box">
        {propertyId && buildingName ? (
          <table className="ri-tbl">
            <thead>
              <tr>
                {cols.map((col) => {
                  const w = cw(col.key);
                  return (
                    <th key={col.key} className={col.sticky ? 'ri-sticky' : ''} style={{ width: w, minWidth: w }}>
                      <div className="th-inner">
                        {col.label}
                        {col.key !== 'expand' && col.key !== 'actions' && <ResizeHandle onResize={(delta) => adjustWidth(col.key, delta)} />}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={cols.length} className="ri-loading-cell">
                  <div className="ri-loading"><Spinner className="ri-spinner--blue" /><span>Loading inventory…</span></div>
                </td></tr>
              )}

              {!loading && showNewRow && (
                <EditableRow
                  rowData={emptyItem()} columns={cols} colWidths={colWidths} isNew
                  requestTypeOptions={requestTypeOptions} propertyId={propertyId}
                  onSave={handleSave} onCancel={() => setShowNewRow(false)}
                />
              )}

              {!loading && paged.length === 0 && !showNewRow && (
                <tr><td colSpan={cols.length} className="ri-empty-cell">
                  <div className="ri-empty">
                    <div className="ri-empty__icon-wrap"><FeatherIcon icon="package" size={28} color="#3B82F6" /></div>
                    <Typography className="ri-empty__title">{searchTerm ? 'No Items Match' : 'No Inventory Items'}</Typography>
                    <Typography className="ri-empty__sub">
                      {searchTerm
                        ? 'Try adjusting your search.'
                        : canEdit ? 'Click "Add Item" to create the first entry.' : 'No inventory recorded for this building.'}
                    </Typography>
                  </div>
                </td></tr>
              )}

              {!loading && paged.map((row) => {
                const isEditing = editingRowId === row._id;
                const isExpanded = expandedRowId === row._id && !isEditing;
                const expiry = getWarrantyExpiry(row);
                const isLowStock = Number(row.quantity?.min) > 0 && Number(row.quantity?.active) <= Number(row.quantity?.min);
                const rowCls = ['ri-row', isExpanded ? 'ri-row--expanded' : '', isLowStock ? 'ri-row--low-stock' : '', expiry && isExpired(expiry) ? 'ri-row--expired' : ''].filter(Boolean).join(' ');

                if (isEditing) return (
                  <EditableRow
                    key={row._id} rowData={row} columns={cols} colWidths={colWidths} isNew={false}
                    requestTypeOptions={requestTypeOptions} propertyId={propertyId}
                    onSave={handleSave} onCancel={() => setEditingRowId(null)}
                  />
                );

                return [
                  <tr key={row._id} className={rowCls} onClick={() => setExpandedRowId(expandedRowId === row._id ? null : row._id)}>
                    {cols.map((col) => {
                      const w = cw(col.key);
                      if (col.key === 'expand') return (
                        <td key="exp" className="ri-expand-td" style={{ width: w }}>
                          <div className={`ri-expand-icon ri-expand-icon--view${isExpanded ? ' ri-expand-icon--open' : ''}`}>
                            <FeatherIcon icon={isExpanded ? 'chevron-down' : 'chevron-right'} size={12} color="#3B82F6" />
                          </div>
                        </td>
                      );
                      if (col.key === 'actions') return (
                        <td key="act" className="ri-actions-td" style={{ width: w }} onClick={(e) => e.stopPropagation()}>
                          <div className="ri-action-group">
                            <Tooltip title="Move item" arrow>
                              <button type="button" className="ri-ebtn ri-ebtn--move" onClick={(e) => { e.stopPropagation(); handleMove(row); }}>
                                <FeatherIcon icon="move" size={13} color="#0D9488" />
                              </button>
                            </Tooltip>
                            {canEdit && (
                              <Tooltip title="Edit item" arrow>
                                <button type="button" className="ri-ebtn" onClick={(e) => { e.stopPropagation(); setEditingRowId(row._id); setExpandedRowId(null); }}>
                                  <FeatherIcon icon="edit-2" size={13} color="#1D4ED8" />
                                </button>
                              </Tooltip>
                            )}
                            {canEdit && (
                              <Tooltip title="Delete item" arrow>
                                <button type="button" className="ri-dbtn" onClick={(e) => { e.stopPropagation(); handleDelete(row); }}>
                                  <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      );
                      return (
                        <td key={col.key} className={col.sticky ? 'ri-sticky' : ''} style={{ width: w }}>
                          <span className="ri-cell-inner">{cellVal(row, col.key, requestTypeOptions)}</span>
                        </td>
                      );
                    })}
                  </tr>,
                  isExpanded && <ExpandedDetailPanel key={`${row._id}-detail`} item={row} requestTypeOptions={requestTypeOptions} colCount={cols.length} />,
                ];
              })}

              {!loading && totals && paged.length > 0 && (
                <tr className="ri-summary">
                  <td colSpan={2} className="ri-summary__label-cell">
                    <span className="ri-summary__label-inner"><FeatherIcon icon="list" size={12} color="#64748B" />{filtered.length} item{filtered.length !== 1 ? 's' : ''}</span>
                  </td>
                  {cols.slice(2).map((col) => {
                    if (col.key === 'costPerUnit') return (
                      <td key={col.key} className="ri-summary__val-cell">
                        <span className="ri-summary__cost">{new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(totals.totalValue)}</span>
                      </td>
                    );
                    if (col.key === 'qty_active') return (
                      <td key={col.key} className="ri-summary__val-cell">
                        <span className="ri-badge ri-badge--warn" style={{ fontSize: '0.67rem' }}>{totals.lowStock} low</span>
                      </td>
                    );
                    return <td key={col.key} className="ri-summary__empty-cell"><span className="ri-cell-muted">—</span></td>;
                  })}
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <div className="ri-empty">
            <div className="ri-empty__icon-wrap"><FeatherIcon icon="archive" size={42} color="#3B82F6" /></div>
            <Typography className="ri-empty__title">Select a Property &amp; Building</Typography>
            <Typography className="ri-empty__sub">Choose a property and building to view or manage recurring inventory items.</Typography>
          </div>
        )}
      </Box>

      {propertyId && buildingName && totalPages > 1 && (
        <div className="ri-pagination-bar">
          <Pagination size="sm" className="ri-pg mb-0">
            <Pagination.Prev onClick={() => currentPage > 1 && setCurrentPage((p) => p - 1)} disabled={currentPage === 1} />
            {[...Array(totalPages)].map((_, i) => (
              <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>{i + 1}</Pagination.Item>
            ))}
            <Pagination.Next onClick={() => currentPage < totalPages && setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}
      {moveModal.open && (
        <MoveItemModal
          item={moveModal.item}
          propertyList={propertyList}
          onConfirm={handleMoveConfirm}
          onClose={handleMoveClose}
          moving={moving}
        />
      )}
    </Paper>
  );
};

export { RecurringInventoryTable };